import { hasAnyOrgRole } from '@/helpers/roles';
import type { Role, User } from '@/types';
import { clsx } from 'clsx';
import React, { type FC, useMemo } from 'react';

import styles from '../styles.module.css';
import type { SidebarLinkProps } from '../types';

import NavigationLink from './NavigationLink';

export type NavigationLinksProps = {
  isInteracting?: boolean;
  /** Array of navigation link configurations */
  links: SidebarLinkProps[];
  /** Current authenticated user */
  user?: User;
  /** Whether the sidebar is currently collapsed */
  isSidebarCollapsed: boolean;
  /** Currently selected organization */
  orgId?: string;
};

/**
 * Filters links based on user roles for a specific org or global roles
 * @param link - The navigation link to check
 * @param orgId - The current org ID (can be undefined)
 * @param userRolesByOrg - The user's roles map (orgId -> Role[])
 * @param globalRoles - The user's global roles (flat array)
 * @returns true if the link should be displayed
 */
const shouldDisplayLink = (
  link: SidebarLinkProps,
  orgId: string | undefined,
  userRolesByOrg: Record<string, Role | Role[]>,
): boolean => {
  const linkRoles = link.roles ?? [];
  if (linkRoles.length === 0) return true;
  return hasAnyOrgRole(userRolesByOrg, orgId, linkRoles);
};

/**
 * Navigation links component for the sidebar
 * Handles role-based filtering and rendering of navigation items
 */
const NavigationLinks: FC<NavigationLinksProps> = ({ isInteracting, links, user, isSidebarCollapsed, orgId }) => {
  // Only org-aware roles supported
  const userRolesByOrg = useMemo(() => user?.assumed_roles ?? {}, [user?.assumed_roles]);

  // Memoize filtered links based on user roles for the selected org
  const visibleLinks = useMemo(
    () => links.filter(link => shouldDisplayLink(link, orgId, userRolesByOrg)),
    [links, orgId, userRolesByOrg],
  );

  // Memoize list className
  const listClassName = useMemo(
    () =>
      clsx(
        styles.sidebarList,
        isSidebarCollapsed && styles.sidebarListIsCollapsed,
        isInteracting && styles.sidebarListInteracting,
      ),
    [isInteracting, isSidebarCollapsed],
  );

  return (
    <ul className={listClassName}>
      {user &&
        visibleLinks.map(link => (
          <NavigationLink key={`link-${link.to}`} link={link} isSidebarCollapsed={isSidebarCollapsed} />
        ))}
    </ul>
  );
};

export default NavigationLinks;
